import type { MealPlan, Recipe, User, ActiveShoppingCart } from '../types';

export function isGuidedUser(user: Pick<User, 'accountMode'> | null | undefined): boolean {
    return user?.accountMode === 'guided';
}

export function isStandardUser(user: Pick<User, 'accountMode'> | null | undefined): boolean {
    return !user?.accountMode || user.accountMode === 'standard';
}

export function canGuidedUserAccessTab(tab: 'plan' | 'recipes' | 'lists' | 'monthly'): boolean {
    return tab !== 'recipes';
}

export function collectPlanRecipeIds(mealPlan: MealPlan, extraPlans: MealPlan[] = []): Set<string> {
    const ids = new Set<string>();
    const allPlans = [mealPlan, ...extraPlans];
    for (const plan of allPlans) {
        for (const day of Object.values(plan)) {
            if (!day) continue;
            for (const mealType of Object.values(day)) {
                for (const meal of mealType || []) {
                    if (meal.recipeId) ids.add(meal.recipeId);
                }
            }
        }
    }
    return ids;
}

export function collectActiveCartRecipeIds(activeCart: ActiveShoppingCart | null | undefined): Set<string> {
    const ids = new Set<string>();
    if (!activeCart?.items) return ids;
    for (const item of activeCart.items) {
        for (const source of item.sources || []) {
            if (source.originType === 'recipe' && source.originId) {
                ids.add(source.originId);
            }
        }
    }
    return ids;
}

export function isRecipeVisibleToGuidedUser(
    recipeId: string,
    mealPlan: MealPlan,
    activeCart: ActiveShoppingCart | null | undefined,
    extraPlans: MealPlan[] = [],
): boolean {
    const allowed = collectPlanRecipeIds(mealPlan, extraPlans);
    for (const id of collectActiveCartRecipeIds(activeCart)) {
        allowed.add(id);
    }
    return allowed.has(recipeId);
}

export function canGuidedUserViewRecipe(
    recipe: Recipe,
    mealPlan: MealPlan,
    activeCart: ActiveShoppingCart | null | undefined,
    extraPlans: MealPlan[] = [],
): boolean {
    return isRecipeVisibleToGuidedUser(recipe.id, mealPlan, activeCart, extraPlans);
}

export const GUIDED_SESSION_KEY = 'mealpai_guided_session';

export interface GuidedSessionCache {
    email: string;
    clientId: string;
    consultantId: string;
    householdId: string;
}

export function readGuidedSession(): GuidedSessionCache | null {
    try {
        const raw = sessionStorage.getItem(GUIDED_SESSION_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as GuidedSessionCache;
        if (!parsed.email || !parsed.clientId || !parsed.consultantId) return null;
        return parsed;
    } catch {
        return null;
    }
}

export function writeGuidedSession(session: GuidedSessionCache): void {
    sessionStorage.setItem(GUIDED_SESSION_KEY, JSON.stringify(session));
}

export function clearGuidedSession(): void {
    sessionStorage.removeItem(GUIDED_SESSION_KEY);
}

export function parseAccessTokenFromPathname(pathname: string): string | null {
    const segment = pathname.replace(/^\/+/, '').split('/')[0]?.trim();
    if (!segment || segment === 'index.html') return null;
    const parts = segment.split('.');
    if (parts.length !== 2 || parts[0].length < 10 || parts[1].length < 10) return null;
    return segment;
}
